import { Box, Stack } from "@mui/material";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";
import { usePacksSelectionLossModal } from "../modals/ConfirmEventSelectionLossModal";
import type { PacksList, PacksType } from "@/types";
import type {
  BucketsType,
  EventPackage,
  EventType,
} from "@/global/store/data/types";
import { PackCard } from "./PackCard";
import { BucketEventsContainer } from "./BucketEventsContainer";
import { BucketAccordion } from "./BucketAccordion";
import { BucketCard } from "./BucketCard";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { BonusUnlocker } from "./bonus-unlocker/BonusUnlocker";

type PacksWrapperProps = {
  // states
  packs: PacksType;
  selectedEvents: EventType[];
  selectedPack: PacksList | undefined;
  selectedPackage: EventPackage | null;

  // functions
  handleSelectPack: (id: number) => void;
  handleOpenPackBucket(bucketId: number): void;
  onClickPreview?: (eventSelected: EventType) => void;
  handleSelect: (event: EventType, bucketId: number) => void;
  handleDeselectEvent: (event: EventType, bucketId: number) => void;
};

export function PacksWrapper({
  packs,
  selectedPack,
  selectedEvents,
  selectedPackage,
  handleSelect,
  onClickPreview,
  handleSelectPack,
  handleDeselectEvent,
  handleOpenPackBucket,
}: PacksWrapperProps) {
  const { md: mdUp } = useCssMediaQuery("up");
  const teamConfig = useTeamConfig();
  const useFeaturedGames = packs?.useFeaturedGames || false;
  const packsSelectionLossModal = usePacksSelectionLossModal();
  const hasBonusUnlockConfig = teamConfig?.packs?.list.some((pack) =>
    Boolean(pack?.unlockBonus?.bonusEventCodes?.length)
  );

  function handleClickPackButton(packId: number) {
    if (
      selectedPack &&
      packId < selectedPack.id &&
      selectedEvents.length > 0 &&
      !teamConfig?.packs?.disablePackSelectionLossModal
    )
      return packsSelectionLossModal.set({ isOpen: true, packId });

    handleSelectPack(packId);
  }

  return (
    <>
      {hasBonusUnlockConfig ? <BonusUnlocker /> : null}
      <Box>
        <Stack
          mt={4}
          spacing={mdUp ? 4 : 2}
          direction={mdUp ? "row" : "column"}
          alignItems="stretch"
        >
          {packs.list.map((pack) => (
            <Box
              key={pack.id}
              sx={{
                display: "flex",
                alignItems: "stretch",
                alignSelf: "stretch",
                flex: 1,
              }}
            >
              <PackCard
                pack={pack}
                isSelected={!!selectedPack && selectedPack.id === pack.id}
                onSelect={handleClickPackButton}
              />
            </Box>
          ))}
        </Stack>

        <Box mt={4}>
          {selectedPack?.buckets.map((bucket, idx) => {
            const bucketEvents = (
              <BucketEventsContainer
                bucket={bucket}
                selectedEvents={selectedEvents}
                useFeaturedGames={useFeaturedGames}
                onClickPreview={onClickPreview}
                onSelect={handleSelect}
                onDeselect={handleDeselectEvent}
              />
            );

            return (
              <Box
                key={bucket.id}
                sx={{ mb: idx < selectedPack.buckets.length - 1 ? 4 : 0 }}
              >
                {bucket?.displayAllEvents ? (
                  <Box pt={mdUp ? 4 : 2}>
                    <BucketCard title={bucket?.title}>
                      {bucketEvents}
                    </BucketCard>
                  </Box>
                ) : (
                  <BucketAccordion
                    bucket={bucket as BucketsType}
                    onToggleBucket={handleOpenPackBucket}
                  >
                    {bucketEvents}
                  </BucketAccordion>
                )}
              </Box>
            );
          })}
        </Box>
      </Box>
    </>
  );
}
